import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Save, Key, Shield } from "lucide-react";
import { api } from "../lib/api";

const PERMS = [
  { id: "leads", label: "Leads Inbox", desc: "Catering + Contact submissions" },
  { id: "seo", label: "SEO Editor", desc: "Page titles, descriptions, meta" },
  { id: "tracking", label: "Tracking Codes", desc: "GA / Pixel / snippets" },
];
const EMPTY = { username: "", password: "", full_name: "", permissions: [] };

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePerm = (id) => setDraft((d) => ({
    ...d,
    permissions: d.permissions.includes(id)
      ? d.permissions.filter((p) => p !== id)
      : [...d.permissions, id],
  }));

  const create = async (e) => {
    e.preventDefault();
    if (draft.password.length < 6) return toast.error("Password must be 6+ chars");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", draft);
      toast.success(`Added ${data.username}`);
      setUsers((u) => [...u, data]);
      setDraft(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const updateUser = async (u, patch) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, patch);
      setUsers((list) => list.map((x) => x.id === u.id ? data : x));
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((list) => list.filter((x) => x.id !== u.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const resetPw = async (u) => {
    const p = window.prompt(`New password for ${u.username} (min 6 chars):`);
    if (!p || p.length < 6) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { password: p });
      toast.success("Password updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  if (loading) return <p className="text-neutral-400 text-lg">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="users-admin">
      {/* Create form */}
      <form onSubmit={create} className="bg-[#141414] border border-white/10 rounded-2xl p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus size={18} className="text-[#ffd100]" />
          <h3 className="font-display text-xl text-white">Add a new user</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input data-testid="new-user-username" required minLength={3} placeholder="Username"
            value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })}
            className="bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffd100] outline-none" />
          <input data-testid="new-user-fullname" placeholder="Full name (optional)"
            value={draft.full_name} onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
            className="bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffd100] outline-none" />
          <input data-testid="new-user-password" required minLength={6} type="password" placeholder="Password (6+ chars)"
            value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })}
            className="bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffd100] outline-none" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {PERMS.map((p) => (
            <button key={p.id} type="button" onClick={() => togglePerm(p.id)}
              data-testid={`new-perm-${p.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold border-2 transition-colors ${
                draft.permissions.includes(p.id)
                  ? "bg-[#ffd100] text-[#0a0a0a] border-[#ffd100]"
                  : "border-white/25 text-neutral-300 hover:border-[#ffd100] hover:text-[#ffd100]"
              }`}>
              {p.label}
            </button>
          ))}
        </div>
        <button type="submit" disabled={saving} data-testid="create-user-btn"
          className="mt-5 inline-flex items-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold px-5 py-2.5 rounded-full hover:bg-[#e5bc00] transition-colors disabled:opacity-60">
          <UserPlus size={16} /> {saving ? "Adding…" : "Add user"}
        </button>
      </form>

      {/* List */}
      <div className="space-y-3">
        {users.map((u) => (
          <UserRow key={u.id} u={u} onUpdate={updateUser} onDelete={del} onResetPw={resetPw} />
        ))}
      </div>
    </div>
  );
}

function UserRow({ u, onUpdate, onDelete, onResetPw }) {
  const [perms, setPerms] = useState(u.permissions);
  const dirty = JSON.stringify([...perms].sort()) !== JSON.stringify([...u.permissions].sort());
  const toggle = (id) => setPerms((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  return (
    <div data-testid={`user-row-${u.username}`} className="bg-[#141414] border border-white/10 rounded-xl p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-display text-lg text-white flex items-center gap-2">
            {u.username}
            {u.role === "owner" && (
              <span className="inline-flex items-center gap-1 bg-[#ffd100]/15 text-[#ffd100] border border-[#ffd100]/40 rounded-full text-xs font-bold px-2.5 py-0.5">
                <Shield size={12} /> OWNER
              </span>
            )}
          </p>
          <p className="text-neutral-400 text-sm">{u.full_name || "—"}</p>
        </div>
        {u.role !== "owner" && (
          <div className="flex gap-2">
            <button data-testid={`reset-pw-${u.username}`} onClick={() => onResetPw(u)}
              className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-3 py-1.5 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors text-sm">
              <Key size={14} /> Reset password
            </button>
            <button data-testid={`delete-user-${u.username}`} onClick={() => onDelete(u)}
              className="inline-flex items-center gap-2 border-2 border-red-500/40 text-red-300 font-semibold px-3 py-1.5 rounded-full hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors text-sm">
              <Trash2 size={14} /> Delete
            </button>
          </div>
        )}
      </div>
      {u.role !== "owner" && (
        <>
          <div className="mt-4 flex flex-wrap gap-2">
            {PERMS.map((p) => (
              <button key={p.id} type="button" onClick={() => toggle(p.id)}
                data-testid={`perm-${u.username}-${p.id}`}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold border-2 transition-colors ${
                  perms.includes(p.id) ? "bg-[#ffd100] text-[#0a0a0a] border-[#ffd100]" : "border-white/25 text-neutral-300 hover:border-[#ffd100] hover:text-[#ffd100]"
                }`}>
                {p.label}
              </button>
            ))}
          </div>
          {dirty && (
            <button onClick={() => onUpdate(u, { permissions: perms })}
              data-testid={`save-perms-${u.username}`}
              className="mt-3 inline-flex items-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold px-4 py-2 rounded-full hover:bg-[#e5bc00] transition-colors text-sm">
              <Save size={14} /> Save changes
            </button>
          )}
        </>
      )}
    </div>
  );
}
